import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import TopBar from './TopBar';
import ConfirmModal from './ConfirmModal';
import { getCartItems, updateCartItemQuantityWithCustomer, removeFromCartWithCustomer, clearCart, persistCartToAppwrite, getVendorFromCache, setVendorInCache, fetchAndValidateCartItems, clearFoodCacheInRedis } from '../services/cartService';
import { getFoodItemById, getFoodItemsByIds, getFoodImageUrl, updateStockCount } from '../services/foodItemService';
import { getProfileByUserId, updateWalletBalance, updateLoyaltyPoints } from '../services/profileService';
import { getVendorApplicationByProfileId } from '../services/vendorApplicationService';
import { createCompleteOrder } from '../services/orderService';
import './CheckoutPage.css';

const CheckoutPage = () => {
  const navigate = useNavigate();
  const { user, profile, refreshCart, refreshProfile, cartRefreshTrigger } = useAuth();
  const { showError, showSuccess } = useToast();

  const [cartItems, setCartItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [pointsToRedeem, setPointsToRedeem] = useState('');
  const [redeemedAmount, setRedeemedAmount] = useState(0);
  const [processingPayment, setProcessingPayment] = useState(false);

  // Confirmation modal state
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [itemToRemove, setItemToRemove] = useState(null);

  // Fetch cart items from database (Optimized via Redis)
  useEffect(() => {
    const fetchCartData = async () => {
      if (!user) {
        navigate('/login');
        return;
      }

      try {
        setLoading(true);
        setError(null);

        // Optimized fetch: This hits Redis first and handles all validation/enrichment
        // It drastically reduces database READs from O(Items) down to O(1)
        const { items, removedCount } = await fetchAndValidateCartItems(user.$id);
        console.log('[Checkout] Items optimized fetch:', items.length);

        if (removedCount > 0) {
          showError(`${removedCount} item(s) were removed because they are no longer available.`);
        }

        if (items.length === 0) {
          setCartItems([]);
          setLoading(false);
          return;
        }

        // Map service format to component state format (if slightly different)
        const formattedItems = items.map(item => ({
          ...item,
          cartItemId: item.id, // Service 'id' is the cart entry ID
          id: item.foodId,     // Service 'foodId' is the unique food ID
          pricePerUnit: item.price
        }));

        setCartItems(formattedItems);
      } catch (err) {
        console.error('Error fetching optimized cart data:', err);
        setError('Failed to load cart items. Please try again.');
        showError(err, 'Failed to load cart items');
      } finally {
        setLoading(false);
      }
    };

    fetchCartData();
  }, [user, navigate, showError, cartRefreshTrigger]); // Respond to global cart updates

  // Get user wallet balance and points from profile
  const walletBalance = profile?.wallet_balance || 0;
  const availablePoints = profile?.loyalty_points || 0;
  const pointsValue = availablePoints / 50; // 50 points = RM 1

  // Format pickup time helper
  const formatPickupTime = (timeString) => {
    if (!timeString) return '';
    try {
      const date = new Date(timeString);
      if (isNaN(date.getTime())) return timeString;
      return date.toLocaleDateString('en-US', {
        day: 'numeric',
        month: 'short',
        year: 'numeric'
      }) + ', ' + date.toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      });
    } catch {
      return timeString;
    }
  };

  // Calculate totals
  const calculateSubtotal = () => {
    return cartItems.reduce((total, item) => total + (item.pricePerUnit * item.quantity), 0);
  };

  // Hardcoded service fee rate (3%)
  // NOTE: `service_fee_percent` was removed from profiles; using a hardcoded
  // constant until a dynamic config is reintroduced.
  const SERVICE_FEE_RATE = 0.03; // 3% (hardcoded)

  const calculateServiceFee = () => {
    // Calculate service fee using the hardcoded rate
    const subtotal = calculateSubtotal();
    return subtotal * SERVICE_FEE_RATE;
  };

  const subtotal = calculateSubtotal();
  const serviceFee = calculateServiceFee();
  const totalBeforePoints = subtotal + serviceFee;
  const remainingToPay = totalBeforePoints - redeemedAmount;

  // Debouncing for quantity updates
  const debounceTimers = React.useRef({});

  const handleQuantityChange = async (cartItemId, newQuantity) => {
    if (newQuantity < 1) {
      setItemToRemove(cartItemId);
      setShowConfirmModal(true);
      return;
    }

    // 1. UPDATE LOCAL STATE IMMEDIATELY (Optimistic UI)
    setCartItems(prev => prev.map(item =>
      item.cartItemId === cartItemId
        ? { ...item, quantity: newQuantity }
        : item
    ));

    // 2. DEBOUNCE THE SERVER SYNC
    if (debounceTimers.current[cartItemId]) {
      clearTimeout(debounceTimers.current[cartItemId]);
    }

    debounceTimers.current[cartItemId] = setTimeout(async () => {
      try {
        await updateCartItemQuantityWithCustomer(user.$id, cartItemId, newQuantity);
        console.log(`[Checkout] Quantity synced for ${cartItemId}: ${newQuantity}`);
        delete debounceTimers.current[cartItemId];
        
        // Refresh cart in context avoided to prevent loop
        // if (refreshCart) refreshCart();
      } catch (error) {
        console.error('Error syncing quantity:', error);
        showError(error, 'Failed to sync quantity with server.');
        // Consider reverting state if critical
      }
    }, 500); // 500ms delay
  };

  const handleDeleteClick = (cartItemId) => {
    setItemToRemove(cartItemId);
    setShowConfirmModal(true);
  };

  const confirmRemove = async () => {
    if (itemToRemove) {
      await handleRemoveItem(itemToRemove);
      setShowConfirmModal(false);
      setItemToRemove(null);
    }
  };

  const cancelRemove = () => {
    setShowConfirmModal(false);
    setItemToRemove(null);
  };

  const handleRemoveItem = async (cartItemId) => {
    try {
      await removeFromCartWithCustomer(user.$id, cartItemId);

      // Update local state
      setCartItems(prev => prev.filter(item => item.cartItemId !== cartItemId));
      showSuccess('Item removed from cart');
      
      // Refresh cart in context to keep other components (TopBar) in sync
      if (refreshCart) refreshCart();
    } catch (error) {
      console.error('Error removing item:', error);
      showError(error, 'Failed to remove item. Please try again.');
    }
  };

  const handleApplyPoints = () => {
    const points = parseInt(pointsToRedeem);
    if (isNaN(points) || points <= 0) {
      showError('Please enter a valid number of points');
      return;
    }
    if (points > availablePoints) {
      showError('Insufficient points');
      return;
    }

    // Convert points to dollar value (100 points = $1)
    const dollarValue = points / 50;

    if (dollarValue > totalBeforePoints) {
      showError('Points value exceeds order total');
      return;
    }

    setRedeemedAmount(dollarValue);
  };

  const handlePayNow = async () => {
    if (cartItems.length === 0) {
      showError('Your cart is empty');
      return;
    }

    if (remainingToPay > walletBalance) {
      showError(`Insufficient wallet balance. You need RM ${(remainingToPay - walletBalance).toFixed(2)} more.`);
      return;
    }

    setProcessingPayment(true);

    try {
      // Validate stock availability (fetch latest stock counts in ONE batch call)
      const foodIds = cartItems.map(it => it.id);
      const freshFoodDocs = await getFoodItemsByIds(foodIds);
      const freshStockMap = {};
      freshFoodDocs.forEach(doc => freshStockMap[doc.$id] = doc.stock_count);

      for (const item of cartItems) {
        const available = freshStockMap[item.id] !== undefined ? freshStockMap[item.id] : 0;
        if (available < item.quantity) {
          const errMsg = `[Stock Validation] ${item.name} only has ${available} left. Please adjust your order.`;
          showError(errMsg);
          setProcessingPayment(false);
          return;
        }
      }

      // Prepare order data for database submission
      const orderData = {
        customerId: user.$id,
        totalAmount: remainingToPay,
        pointsRedeemed: pointsToRedeem ? parseInt(pointsToRedeem) : 0,
        pickupId: cartItems[0]?.pickupId || null // Use first item's pickup point
      };

      // Prepare items for order
      const orderItems = cartItems.map(item => ({
        foodId: item.id,
        quantity: item.quantity,
        pricePerUnit: item.pricePerUnit
      }));

      console.log('Creating order:', orderData, orderItems);

      // Create the order and order items
      const result = await createCompleteOrder(orderData, orderItems);
      console.log('Order created successfully:', result);

      // Map foodId -> created order item $id so vendor notifications can reference the order item id(s)
      const orderItemIdByFoodId = {};
      (result?.orderItems || []).forEach(oi => {
        if (oi?.food_id) orderItemIdByFoodId[oi.food_id] = oi.$id;
      });

      // Decrement stock counts for purchased items (best-effort)
      let stockUpdateFailed = false;
      try {
        // Fetch all fresh food items in one READ call
        const foodIds = cartItems.map(it => it.id);
        const freshFoodItems = await getFoodItemsByIds(foodIds);
        const freshMap = {};
        freshFoodItems.forEach(f => freshMap[f.$id] = f);

        for (const item of cartItems) {
          try {
            const fresh = freshMap[item.id];
            const current = fresh?.stock_count || 0;
            const newCount = Math.max(0, current - item.quantity);
            await updateStockCount(item.id, newCount);
            console.log(`Stock updated for ${item.id}: ${current} -> ${newCount}`);
            
            // 3. IMMEDIATELY PURGE SHARED CACHE (Inventory Integrity)
            // This ensures the next person to view the menu gets the fresh count.
            await clearFoodCacheInRedis(user.$id, item.id);
          } catch (stockUpdateErr) {
            console.error(`[Appwrite Inventory Update] Failed for ${item.id}:`, stockUpdateErr);
            stockUpdateFailed = true;
          }
        }
      } catch (batchErr) {
        console.error('Failed batch fetch for stock updates', batchErr);
        stockUpdateFailed = true;
      }

      if (stockUpdateFailed) {
        console.warn('One or more stock updates failed; orders were placed but inventory may be inconsistent.');
      }

      // Update customer wallet balance (deduct payment)
      if (profile?.$id) {
        const newWalletBalance = walletBalance - remainingToPay;
        await updateWalletBalance(profile.$id, newWalletBalance);
        console.log('Customer wallet balance updated to:', newWalletBalance);

        // Update loyalty points (subtract redeemed, add earned)
        const redeemedPoints = pointsToRedeem ? parseInt(pointsToRedeem) : 0;
        const earnedPoints = Math.floor(remainingToPay);
        const newPoints = Math.max(0, availablePoints - redeemedPoints + earnedPoints);

        await updateLoyaltyPoints(profile.$id, newPoints);
        console.log('Loyalty points updated:', {
          previous: availablePoints,
          redeemed: redeemedPoints,
          earned: earnedPoints,
          final: newPoints
        });
      }

      // Update vendor wallets (credit payment) and Notify vendors
      const vendorTotals = {};
      const vendorItemsMap = {};
      for (const item of cartItems) {
        const vendorId = item.vendorId;
        if (!vendorId) continue;

        const itemTotal = item.pricePerUnit * item.quantity;
        vendorTotals[vendorId] = (vendorTotals[vendorId] || 0) + itemTotal;

        if (!vendorItemsMap[vendorId]) vendorItemsMap[vendorId] = [];
        vendorItemsMap[vendorId].push({ foodId: item.id, name: item.name, quantity: item.quantity, pricePerUnit: item.pricePerUnit });
      }

      for (const [vendorId, vendorTotal] of Object.entries(vendorTotals)) {
        try {
          // 1. Get vendor profile/notification info (Try cache first to save READs)
          let vendorData = await getVendorFromCache(user.$id, vendorId);
          let vendorProfile = vendorData?.profile;
          let vendorEmail = vendorData?.email;

          if (!vendorProfile) {
            console.log(`[Checkout] Vendor cache miss for ${vendorId}, fetching from Appwrite...`);
            vendorProfile = await getProfileByUserId(vendorId);
            if (vendorProfile) {
              try {
                const vendorApp = await getVendorApplicationByProfileId(vendorProfile.$id);
                vendorEmail = vendorApp?.contact_email || vendorProfile.email;
              } catch (appErr) {
                vendorEmail = vendorProfile.email;
              }
              // Cache for 24h
              await setVendorInCache(user.$id, vendorId, { profile: vendorProfile, email: vendorEmail });
            }
          }

          if (vendorProfile) {
            // 2. Update wallet (We need fresh balance, but we just got it or it's cached)
            // Note: Since we need fresh balance for financial safety, even if cached, 
            // we'll rely on the profile we just fetched/found.
            const currentVendorBalance = vendorProfile.wallet_balance || 0;
            const newVendorBalance = currentVendorBalance + vendorTotal;
            await updateWalletBalance(vendorProfile.$id, newVendorBalance);
            console.log(`Vendor ${vendorId} wallet updated: +RM${vendorTotal.toFixed(2)}`);

            // 3. Notify Vendor
            if (vendorProfile.sales_notification !== false) { // Default to true
              const vendorOrderItemIds = (vendorItemsMap[vendorId] || [])
                .map(it => orderItemIdByFoodId[it.foodId])
                .filter(Boolean);

              fetch('/api/notify-vendor', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                  vendorEmail,
                  vendorName: vendorProfile.vendor_name || vendorProfile.full_name || '',
                  orderId: vendorOrderItemIds.length ? vendorOrderItemIds.join(', ') : result?.order?.$id,
                  items: vendorItemsMap[vendorId] || [],
                  total: vendorTotal,
                  customerName: profile?.full_name || user.email,
                  salesNotification: true
                })
              }).catch(err => console.error('[Notification Service] Notify vendor error:', err));
            }
          }
        } catch (vendorError) {
          console.error(`Error processing vendor ${vendorId}:`, vendorError);
        }
      }



      // Clear the cart from Redis and persist empty state to Appwrite
      await clearCart(user.$id);
      await persistCartToAppwrite(user.$id);
      console.log('Cart cleared and persisted to Appwrite');

      // Refresh TopBar cart and profile (wallet balance)
      if (refreshCart) refreshCart();
      if (refreshProfile) await refreshProfile();

      setProcessingPayment(false);
      if (stockUpdateFailed) {
        showSuccess('Payment Successful! Your order has been placed. Note: some inventory updates failed.');
      } else {
        showSuccess('Payment Successful! Your order has been placed.');
      }
      navigate('/orders');
    } catch (error) {
      console.error('[Payment Flow Critical Error]:', error);
      setProcessingPayment(false);
      showError(error, `[Payment Failure] ${error.message}`);
    }
  };

  return (
    <div className="checkout-page">
      <TopBar />
      <div className="checkout-container">
        <div className="checkout-content">
          {/* Heading */}
          <div className="checkout-heading">
            <h1>Checkout</h1>
          </div>
          <div className="checkout-subtitle-container">
            <p className="checkout-subtitle">Complete your order and enjoy your meal</p>
          </div>

          {loading ? (
            <div className="loading-container">
              <div className="loading-spinner"></div>
              <p>Loading your cart...</p>
            </div>
          ) : error ? (
            <div className="error-container">
              <p className="error-message">{error}</p>
              <button onClick={() => window.location.reload()} className="retry-button">
                Retry
              </button>
            </div>
          ) : cartItems.length === 0 ? (
            <div className="empty-cart-container">
              <div className="empty-cart-icon">🛒</div>
              <h2>Your cart is empty</h2>
              <p>Add some delicious food items to get started!</p>
              <button onClick={() => navigate('/buy-food')} className="browse-food-btn">
                Browse Food
              </button>
            </div>
          ) : (
            <>


              {/* Order Summary Section */}
              <div className="checkout-section">
                <div className="section-header">
                  <span className="section-icon cart-icon">🛒</span>
                  <h2>Order Summary</h2>
                </div>

                <div className="order-items-list">
                  {cartItems.map((item) => (
                    <div key={item.cartItemId} className="order-item-card">
                      {item.image ? (
                        <img src={item.image} alt={item.name} className="item-image" />
                      ) : (
                        <div className="item-image-placeholder">🍽️</div>
                      )}
                      <div className="item-info">
                        <h3 className="item-name">{item.name}</h3>
                        <div className="item-quantity-controls">
                          <button
                            onClick={() => handleQuantityChange(item.cartItemId, item.quantity - 1)}
                            className="quantity-btn"
                          >
                            −
                          </button>
                          <span className="quantity-display">{item.quantity}</span>
                          <button
                            onClick={() => handleQuantityChange(item.cartItemId, item.quantity + 1)}
                            className="quantity-btn"
                            disabled={item.quantity >= item.stockCount}
                          >
                            +
                          </button>
                        </div>
                        <div className="item-price-per-unit">RM {item.pricePerUnit.toFixed(2)} each</div>

                        <div className="item-pickup-location">
                          <span className="pickup-label">Pickup Location:</span>
                          <span className="pickup-value">{item.pickupLocation || 'Not specified'}</span>
                          {item.pickupTime && (
                            <span className="pickup-time">• {formatPickupTime(item.pickupTime)}</span>
                          )}
                        </div>
                      </div>
                      <div className="item-actions">
                        <div className="item-total">RM {(item.pricePerUnit * item.quantity).toFixed(2)}</div>
                        <button
                          onClick={() => handleDeleteClick(item.cartItemId)}
                          className="remove-item-btn"
                        >
                          🗑️ Remove
                        </button>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="order-summary-totals">
                  <div className="summary-line">
                    <span>Subtotal:</span>
                    <span>RM {subtotal.toFixed(2)}</span>
                  </div>
                  <div className="summary-line">
                    <span>{`Service Fee (${SERVICE_FEE_RATE * 100}%):`}</span>
                    <span>RM {serviceFee.toFixed(2)}</span>
                  </div>
                  <div className="summary-line total-line">
                    <span>Total:</span>
                    <span>RM {totalBeforePoints.toFixed(2)}</span>
                  </div>
                </div>
              </div>

              {/* Payment Details Section */}
              <div className="checkout-section">
                <div className="section-header">
                  <span className="section-icon payment-icon">💳</span>
                  <h2>Payment Details</h2>
                </div>

                <div className="wallet-balance-card">
                  <span>E-Wallet Balance:</span>
                  <span className="balance-value">RM {walletBalance.toFixed(2)}</span>
                </div>

                <div className="redeem-points-section">
                  <label htmlFor="points-input" className="points-label">Redeem Points</label>
                  <div className="points-input-wrapper">
                    <input
                      type="text"
                      id="points-input"
                      className="points-input"
                      placeholder="Enter points"
                      value={pointsToRedeem}
                      onChange={(e) => setPointsToRedeem(e.target.value)}
                    />
                    <button className="apply-button" onClick={handleApplyPoints}>
                      Apply
                    </button>
                  </div>
                  <div className="points-available-text">
                    Available: {availablePoints.toLocaleString()} points (RM {pointsValue.toFixed(2)} value)
                  </div>
                  <p className="points-info-text">
                    💡 Every 50 points you redeem gives you RM 1.00 discount on your order.
                  </p>
                </div>

                <div className="payment-summary-section">
                  <div className="payment-line">
                    <span>Subtotal:</span>
                    <span>RM {totalBeforePoints.toFixed(2)}</span>
                  </div>
                  {redeemedAmount > 0 && (
                    <div className="payment-line redeemed">
                      <span>Points Redeemed:</span>
                      <span>-RM {redeemedAmount.toFixed(2)}</span>
                    </div>
                  )}
                  <div className="payment-line remaining-line">
                    <span>Remaining to Pay:</span>
                    <span className="remaining-value">RM {remainingToPay.toFixed(2)}</span>
                  </div>
                  <div className="payment-line points-earned-line">
                    <span>Points to be claimed:</span>
                    <span className="points-earned-value">✨ {Math.floor(remainingToPay)} pts</span>
                  </div>
                </div>

                <button
                  className="pay-button"
                  onClick={handlePayNow}
                  disabled={processingPayment || remainingToPay > walletBalance}
                >
                  {processingPayment ? (
                    <>
                      <span className="spinner-small"></span> Processing...
                    </>
                  ) : (
                    <>
                      <span className="pay-icon">💳</span>
                      Pay Now - RM {remainingToPay.toFixed(2)}
                    </>
                  )}
                </button>
                {remainingToPay > walletBalance && (
                  <p className="insufficient-balance-warning">
                    ⚠️ Insufficient wallet balance. Please top up your wallet.
                  </p>
                )}
              </div>
            </>
          )}
        </div>
      </div>

      <ConfirmModal
        isOpen={showConfirmModal}
        onConfirm={confirmRemove}
        onCancel={cancelRemove}
        title="Remove Item"
        message="Remove this item from cart?"
      />
    </div>
  );
};

export default CheckoutPage;
